import { ReactElement, ReactNode, createRef, useEffect, useState } from 'react'
import styled from 'styled-components'

const TabsWrapper = styled.div`
  border-bottom: 1px solid #e3e3e3;
`

const TabsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
`

const TabItem = styled.li<{ isActive?: boolean }>`
  color: ${({ isActive }): string => (isActive ? '#5d59d7' : '#717171')};
  cursor: pointer;
  display: inline-block;
  font-size: 16px;
  padding: 10px 12px;
  ${({ isActive }): string => (isActive ? 'font-weight: 500;' : '')}
`

export const ActiveLine = styled.div<{ duration: number; offset: number; width: number }>`
  background: #5d59d7;
  height: 2px;
  transform: translateX(${({ offset }): string => `${offset}px`});
  transition: all ${({ duration }): string => `${duration}ms`} cubic-bezier(0.15, 0.3, 0.25, 1);
  width: ${({ width }): string => `${width}px`};
`

interface ITabSelectorProps {
  items?: string[]
  activeIndex?: number
  duration?: number
  onChange?: (index: number) => void
}

export const TabsSelector = ({
  items = [],
  activeIndex = 0,
  duration = 350,
  onChange
}: ITabSelectorProps): ReactElement => {
  const activeRef = createRef<HTMLLIElement>()
  const none = createRef<HTMLLIElement>()
  const [offset, setOffset] = useState(0)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const activeElement = activeRef.current
    setOffset(activeElement?.offsetLeft ?? 0)
    setWidth(activeElement?.clientWidth ?? 0)
  }, [activeIndex, activeRef])

  return (
    <TabsWrapper>
      <TabsList>
        {items.map((item, index) => {
          return (
            <TabItem
              key={item}
              ref={activeIndex === index ? activeRef : none}
              isActive={activeIndex === index}
              onClick={(): void => onChange?.(index)}
            >
              {item}
            </TabItem>
          )
        })}
      </TabsList>
      <ActiveLine duration={duration} offset={offset} width={width} />
    </TabsWrapper>
  )
}

const TabViewWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`

const TabViewItem = styled.div<{ duration: number; offset: number }>`
  display: flex;
  flex-wrap: no-wrap;
  width: 100%;

  transform: translateX(${({ offset }): string => `${offset}%`});
  transition: transform ${({ duration }): string => `${duration}ms`};

  & > div {
    flex-shrink: 0;
    width: 100%;
  }
`

interface ITabViewProps {
  children: ReactNode
  activeIndex?: number
  duration?: number
}

export const TabView = ({ children, activeIndex = 0, duration = 300 }: ITabViewProps): ReactElement => {
  const offset = -100 * activeIndex

  return (
    <TabViewWrapper>
      <TabViewItem duration={duration} offset={offset}>
        {children}
      </TabViewItem>
    </TabViewWrapper>
  )
}
